import { useState, useEffect, useMemo } from 'react';
import type { FamilyMember, FamilyMemberFormData, RelationshipType } from '../types/family';
import { US_STATES, calculateAge } from '../utils/stateConsentAges';

interface FamilyMemberFormProps {
  initialData?: FamilyMember;
  onSubmit: (data: FamilyMemberFormData) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
}

export default function FamilyMemberForm({
  initialData,
  onSubmit,
  onCancel,
  loading,
  error,
}: FamilyMemberFormProps) {
  const [firstName, setFirstName] = useState(initialData?.first_name ?? '');
  const [lastName, setLastName] = useState(initialData?.last_name ?? '');
  const [dob, setDob] = useState(initialData?.date_of_birth ?? '');
  const [relationship, setRelationship] = useState<RelationshipType>(
    initialData?.relationship ?? 'child'
  );
  const [state, setState] = useState(initialData?.state ?? 'NY');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    queueMicrotask(() => setFormError(null));
  }, [firstName, lastName, dob, relationship]);

  const ageWarning = useMemo(() => {
    if (!dob) return null;
    const age = calculateAge(dob);
    if (age < 0) return null;
    if (relationship === 'child' && age >= 30) {
      return `This person is ${age} years old, did you mean Spouse?`;
    }
    if (relationship === 'spouse' && age < 16) {
      return `This person is ${age} years old, did you mean Child?`;
    }
    return null;
  }, [dob, relationship]);

  const handleSubmit = async () => {
    setFormError(null);

    if (!firstName.trim() || firstName.trim().length < 2) {
      setFormError('First name must be at least 2 characters.');
      return;
    }
    if (!lastName.trim() || lastName.trim().length < 2) {
      setFormError('Last name must be at least 2 characters.');
      return;
    }
    if (!dob) {
      setFormError('Date of birth is required.');
      return;
    }

    // Validate DOB is in the past
    const dobDate = new Date(dob);
    if (dobDate >= new Date()) {
      setFormError('Date of birth must be in the past.');
      return;
    }

    await onSubmit({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      date_of_birth: dob,
      relationship,
      state,
      email: null,
      phone: null,
    });
  };

  const displayError = formError || error;

  return (
    <div className="family-form">
      <div className="family-form-row">
        <label>Relationship</label>
        <div className="family-relationship-toggle">
          <button
            type="button"
            className={`family-rel-btn ${relationship === 'child' ? 'active' : ''}`}
            onClick={() => setRelationship('child')}
          >
            <span>👶</span> Child
          </button>
          <button
            type="button"
            className={`family-rel-btn ${relationship === 'spouse' ? 'active' : ''}`}
            onClick={() => setRelationship('spouse')}
          >
            <span>💑</span> Spouse
          </button>
        </div>
      </div>

      <div className="family-form-row-pair">
        <div className="family-form-row">
          <label htmlFor="fm-first">First Name</label>
          <input
            id="fm-first"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
          />
        </div>
        <div className="family-form-row">
          <label htmlFor="fm-last">Last Name</label>
          <input
            id="fm-last"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
          />
        </div>
      </div>

      <div className="family-form-row">
        <label htmlFor="fm-dob">Date of Birth</label>
        <input
          id="fm-dob"
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
        />
        {ageWarning && (
          <span className="family-form-warning">{ageWarning}</span>
        )}
      </div>

      <div className="family-form-row">
        <label htmlFor="fm-state">State of Residence</label>
        <select
          id="fm-state"
          value={state}
          onChange={(e) => setState(e.target.value)}
        >
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {displayError && <p className="form-error">{displayError}</p>}

      <div className="family-form-actions">
        <button
          type="button"
          className="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading
            ? 'Saving…'
            : initialData
            ? 'Update'
            : 'Add Family Member'}
        </button>
      </div>
    </div>
  );
}
